// Thin fetch wrapper. The base URL is empty in dev (Vite proxies /api → backend)
// and in prod (Traefik IngressRoute serves both on the same host).

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

// Admin /sessions response shape — matches backend SessionOut. Shared
// between createSession, listSessions, getSession so the dashboard pages
// don't redeclare overlapping inline types.
export type AdminSession = {
  id: string;
  org_id: string;
  join_code: string;
  // Visitor-facing join URL, built server-side from ASKTOGETHER_PUBLIC_BASE_URL.
  // Always use this for share/QR affordances — never synthesise from
  // window.location.origin, which leaks the OAuth-walled admin host
  // (asktogether.downloadserver.co.uk) into links handed to visitors.
  join_url: string;
  name: string;
  state: string;
  starts_at: string | null;
  settings: Record<string, unknown>;
  created_at: string;
};

// Current organiser identity from /api/v1/admin/me. Drives the
// role-aware top-nav (admins see Operators tab; operators see My
// Sessions; moderators see only assigned sessions).
export type AdminMe = {
  user_id: string;
  org_id: string;
  email: string;
  role: "admin" | "operator" | "moderator";
};

// Public runtime config (no auth needed). `auth_mode` drives how AdminShell
// recovers a 401: in saas mode it sends the user to /login for a fresh
// magic-link; in cluster mode it tells them to refresh through OAuth.
export type PublicConfig = {
  auth_mode: "header" | "magic_link";
  public_base_url: string;
};

export type Operator = {
  user_id: string;
  email: string;
  role: "operator";
  created_at: string;
};

export type RevokedOperator = {
  user_id: string;
  email: string;
  role: "revoked";
  created_at: string;
};

export type ClusterFeedbackSummary = {
  confirmed_count: number;
  rejected_count: number;
  current_threshold: number;
  suggested_threshold: number | null;
};

export type SearchHit = {
  question_id: string;
  score: number;
};

export type SessionModeratorRow = {
  user_id: string;
  email: string;
  role: "admin" | "operator" | "moderator";
  via: "explicit" | "session_owner_admin" | "session_creator_operator";
  added_at: string;
};

export type PendingInvite = {
  invite_id: string;
  email: string;
  role: string;
  invite_url: string;
  expires_at: string;
  created_at: string;
};

export type InviteCreated = {
  invite_id: string;
  token: string;
  invite_url: string;
  email: string;
  role: string;
  expires_at: string;
};

export type InvitePreview = {
  role: "operator" | "moderator";
  email: string;
  inviter_email: string;
  session_id: string | null;
  session_name: string | null;
  expires_at: string;
  accepted: boolean;
};

const TOKEN_KEY = "asktogether.token";
const SESSION_KEY = "asktogether.session";
const UPVOTED_KEY_PREFIX = "asktogether.upvoted.";

export const tokenStore = {
  set(token: string) {
    sessionStorage.setItem(TOKEN_KEY, token);
  },
  get(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  },
  clear() {
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(SESSION_KEY);
  },
};

// Locally cached session metadata captured at join time. `displayName` is
// what the attendee typed (or null if they joined anonymously); the board
// renders this as a "Joined as: Name" pill so the attendee always knows
// which identity they're acting under.
type StoredSession = {
  id: string;
  name: string;
  state: string;
  participantId: string;
  displayName: string | null;
  isAnonymous: boolean;
};

export const sessionStore = {
  set(payload: StoredSession) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
  },
  get(): StoredSession | null {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    // Tolerate older payloads written by previous frontend builds before
    // displayName/isAnonymous were captured. They survive the upgrade as
    // anonymous-with-no-name; users can rejoin to refresh the cache.
    return {
      id: parsed.id ?? "",
      name: parsed.name ?? "",
      state: parsed.state ?? "",
      participantId: parsed.participantId ?? "",
      displayName: parsed.displayName ?? null,
      isAnonymous: parsed.isAnonymous ?? false,
    };
  },
};

// Per-session record of which question ids this attendee has voted on. The
// backend deduplicates votes server-side (one Vote row per participant per
// question, and "join" rows count too), but doesn't tell the client which
// questions the participant already counts toward — so the board can't
// reflect "you've voted" state on its own. Mirroring that locally keeps the
// upvote button truthful: filled + disabled once voted, surviving page
// refreshes within the same browser session.
export const upvotedStore = {
  list(sessionId: string): string[] {
    const raw = sessionStorage.getItem(UPVOTED_KEY_PREFIX + sessionId);
    return raw ? (JSON.parse(raw) as string[]) : [];
  },
  add(sessionId: string, questionId: string) {
    const set = new Set(this.list(sessionId));
    set.add(questionId);
    sessionStorage.setItem(
      UPVOTED_KEY_PREFIX + sessionId,
      JSON.stringify([...set]),
    );
  },
};

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = tokenStore.get();
  // Custom header (not Authorization) because the cluster's oauth2-proxy
  // overwrites Authorization with its own OAuth ID token before forwarding,
  // which would clobber the attendee bearer. See backend/auth.py for the
  // matching read path.
  if (token) headers["X-Attendee-Token"] = token;

  const r = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) {
    let detail = `${r.status} ${r.statusText}`;
    try {
      const j = await r.json();
      if (j?.detail) detail = String(j.detail);
    } catch {
      // ignore
    }
    throw new ApiError(r.status, detail);
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  // Runtime configuration probe — public, unauthenticated. Used at app
  // mount to decide whether 401s should route to /login (saas) or tell
  // the user to refresh through OAuth (cluster).
  config() {
    return request<PublicConfig>("GET", `/api/v1/config`);
  },

  // SaaS-mode magic-link sign-in. The backend always returns 204 (no
  // enumeration) — the user finishes signing in by clicking the link in
  // their inbox. Only registered in deploy_mode=saas; in cluster mode
  // this returns 404, which Login.tsx surfaces as "magic-link sign-in is
  // not enabled for this deploy".
  requestMagicLink(email: string) {
    return request<void>("POST", `/api/v1/auth/request-link`, { email });
  },
  logout() {
    return request<void>("POST", `/api/v1/auth/logout`);
  },

  sessionInfo(sessionId: string) {
    return request<{ id: string; name: string; state: string; starts_at: string | null }>(
      "GET",
      `/api/v1/sessions/${sessionId}/info`,
    );
  },

  joinByCode(code: string, payload: { display_name: string | null; is_anonymous: boolean }) {
    return request<{
      token: string;
      participant_id: string;
      session_id: string;
      session_name: string;
      session_state: string;
    }>("POST", `/api/v1/sessions/by-code/${encodeURIComponent(code)}/join`, payload);
  },

  // Authoritative identity for the current attendee bearer token. Used by
  // the board to render "Joined as: …" without trusting cached sessionStore
  // state, which may have been written by an older frontend build.
  me() {
    return request<{
      participant_id: string;
      session_id: string;
      display_name: string | null;
      is_anonymous: boolean;
    }>("GET", `/api/v1/me`);
  },

  similarity(sessionId: string, draft: string) {
    return request<{
      candidates: {
        question_id: string;
        body: string;
        score: number;
        vote_count: number;
        voices_count: number;
        upvote_count: number;
        status: string;
      }[];
      threshold: number;
      // Phase 5 live drafts. Empty unless the session has drafts_mode
      // set to something other than "off". `preview` is null in
      // generic mode, truncated text in preview mode, full text in
      // full mode — the UI renders whichever it gets.
      drafts: { score: number; preview: string | null }[];
    }>("POST", `/api/v1/sessions/${sessionId}/similarity`, { draft_text: draft });
  },

  submitQuestion(sessionId: string, body: string) {
    return request<{ id: string; body: string; status: string; vote_count: number; upvote_count: number; askers?: string[]; anonymous_count?: number }>(
      "POST",
      `/api/v1/sessions/${sessionId}/questions`,
      { body },
    );
  },

  joinQuestion(qid: string) {
    return request<{ id: string; body: string; status: string; vote_count: number; upvote_count: number; askers?: string[]; anonymous_count?: number }>(
      "POST",
      `/api/v1/questions/${qid}/join`,
    );
  },

  upvote(qid: string) {
    return request<{ id: string; body: string; status: string; vote_count: number; upvote_count: number; askers?: string[]; anonymous_count?: number }>(
      "POST",
      `/api/v1/questions/${qid}/votes`,
    );
  },

  board(sessionId: string) {
    return request<
      {
        id: string;
        body: string;
        status: string;
        vote_count: number;
        upvote_count: number;
        askers: string[];
        anonymous_count: number;
        answered_at: string | null;
        answer_text: string | null;
        created_at: string;
      }[]
    >("GET", `/api/v1/sessions/${sessionId}/board`);
  },

  // Organiser routes — under /api/v1/admin/* so the cluster Ingress can
  // OAuth-wall them with a single path-prefix match. The browser carries
  // the access-manager OAuth cookie automatically.
  listSessions() {
    return request<AdminSession[]>("GET", `/api/v1/admin/sessions`);
  },

  getSession(sid: string) {
    return request<AdminSession>("GET", `/api/v1/admin/sessions/${sid}`);
  },

  createSession(payload: {
    name: string;
    settings?: Record<string, unknown>;
    starts_at?: string | null;
  }) {
    return request<AdminSession>("POST", `/api/v1/admin/sessions`, payload);
  },

  scheduleSession(sid: string, starts_at: string | null) {
    return request<{
      id: string;
      state: string;
      starts_at: string | null;
    }>("PATCH", `/api/v1/admin/sessions/${sid}/schedule`, { starts_at });
  },

  // Partial update to Session.settings. Pass `null` to clear an override
  // and fall back to the cluster default; pass a value to set it.
  patchSessionSettings(
    sid: string,
    patch: {
      similarity_threshold?: number | null;
      drafts_mode?: "off" | "generic" | "preview" | "full" | null;
    },
  ) {
    return request<AdminSession>(
      "PATCH",
      `/api/v1/admin/sessions/${sid}/settings`,
      patch,
    );
  },

  transitionSession(sid: string, state: "setup" | "open" | "paused" | "closed") {
    return request<{ id: string; state: string }>(
      "PATCH",
      `/api/v1/admin/sessions/${sid}/state`,
      { state },
    );
  },

  moderationQueue(sid: string, status: "pending" | "approved" | "rejected" = "pending") {
    return request<
      {
        id: string;
        body: string;
        status: string;
        vote_count: number;
        upvote_count: number;
        askers: string[];
        anonymous_count: number;
        answered_at: string | null;
        answer_text: string | null;
        cluster_id: string | null;
        created_at: string;
      }[]
    >("GET", `/api/v1/admin/sessions/${sid}/queue?status=${status}`);
  },

  moderate(
    qid: string,
    decision: "approved" | "rejected" | "pending",
    reason?: string,
  ) {
    return request<{ id: string; status: string }>(
      "POST",
      `/api/v1/admin/questions/${qid}/moderate`,
      { decision, reason },
    );
  },

  clusterFeedback(
    sid: string,
    qa: string,
    qb: string,
    verdict: "similar" | "not_similar",
  ) {
    return request<ClusterFeedbackSummary>(
      "POST",
      `/api/v1/admin/sessions/${sid}/cluster-feedback`,
      { question_a_id: qa, question_b_id: qb, verdict },
    );
  },
  clusterFeedbackSummary(sid: string) {
    return request<ClusterFeedbackSummary>(
      "GET",
      `/api/v1/admin/sessions/${sid}/cluster-feedback/summary`,
    );
  },
  bulkMarkSimilar(sid: string, questionIds: string[]) {
    return request<ClusterFeedbackSummary>(
      "POST",
      `/api/v1/admin/sessions/${sid}/cluster-feedback/bulk-similar`,
      { question_ids: questionIds },
    );
  },
  listClusterFeedbackPairs(sid: string, verdict: "similar" | "not_similar") {
    return request<
      {
        feedback_id: string;
        question_a_id: string;
        question_a_body: string;
        question_b_id: string;
        question_b_body: string;
        verdict: "similar" | "not_similar";
        created_at: string;
      }[]
    >(
      "GET",
      `/api/v1/admin/sessions/${sid}/cluster-feedback/pairs?verdict=${verdict}`,
    );
  },
  deleteClusterFeedback(sid: string, feedbackId: string) {
    return request<null>(
      "DELETE",
      `/api/v1/admin/sessions/${sid}/cluster-feedback/${feedbackId}`,
    );
  },

  // Public semantic search of approved questions in a session — used
  // by the visitor board's filter/relevance affordance. Best-effort
  // server-side: empty result on embedding/qdrant failure.
  boardSearch(sid: string, q: string) {
    return request<SearchHit[]>(
      "GET",
      `/api/v1/sessions/${sid}/board/search?q=${encodeURIComponent(q)}`,
    );
  },
  // Moderator-side semantic search across all questions (any status)
  // in a session. Reorders the queue by relevance to the query.
  adminSessionSearch(sid: string, q: string) {
    return request<SearchHit[]>(
      "GET",
      `/api/v1/admin/sessions/${sid}/search?q=${encodeURIComponent(q)}`,
    );
  },

  markAnswered(qid: string, text: string | null) {
    return request<{
      id: string;
      status: string;
      answered_at: string | null;
      answer_text: string | null;
    }>("POST", `/api/v1/admin/questions/${qid}/answer`, { text });
  },

  unmarkAnswered(qid: string) {
    return request<{
      id: string;
      status: string;
      answered_at: string | null;
      answer_text: string | null;
    }>("DELETE", `/api/v1/admin/questions/${qid}/answer`);
  },

  // ── RBAC: organiser identity + role-management ─────────────────────────
  adminMe() {
    return request<AdminMe>("GET", `/api/v1/admin/me`);
  },

  listOperators() {
    return request<Operator[]>("GET", `/api/v1/admin/operators`);
  },
  listRevokedOperators() {
    return request<RevokedOperator[]>(
      "GET",
      `/api/v1/admin/operators/revoked`,
    );
  },
  listOperatorInvites() {
    return request<PendingInvite[]>("GET", `/api/v1/admin/operators/invites`);
  },
  inviteOperator(email: string) {
    return request<InviteCreated>("POST", `/api/v1/admin/operators/invites`, {
      email,
    });
  },
  cancelOperatorInvite(inviteId: string) {
    return request<void>(
      "DELETE",
      `/api/v1/admin/operators/invites/${inviteId}`,
    );
  },
  revokeOperator(userId: string) {
    return request<void>("DELETE", `/api/v1/admin/operators/${userId}`);
  },
  reinstateOperator(userId: string) {
    return request<Operator>(
      "POST",
      `/api/v1/admin/operators/${userId}/reinstate`,
    );
  },

  listSessionModerators(sid: string) {
    return request<SessionModeratorRow[]>(
      "GET",
      `/api/v1/admin/sessions/${sid}/moderators`,
    );
  },
  listSessionModeratorInvites(sid: string) {
    return request<PendingInvite[]>(
      "GET",
      `/api/v1/admin/sessions/${sid}/moderator-invites`,
    );
  },
  inviteSessionModerator(sid: string, email: string) {
    return request<InviteCreated>(
      "POST",
      `/api/v1/admin/sessions/${sid}/moderator-invites`,
      { email },
    );
  },
  cancelSessionModeratorInvite(sid: string, inviteId: string) {
    return request<void>(
      "DELETE",
      `/api/v1/admin/sessions/${sid}/moderator-invites/${inviteId}`,
    );
  },
  removeSessionModerator(sid: string, userId: string) {
    return request<void>(
      "DELETE",
      `/api/v1/admin/sessions/${sid}/moderators/${userId}`,
    );
  },

  getInvite(token: string) {
    return request<InvitePreview>(
      "GET",
      `/api/v1/invites/${encodeURIComponent(token)}`,
    );
  },
  acceptInvite(token: string) {
    return request<void>(
      "POST",
      `/api/v1/invites/${encodeURIComponent(token)}/accept`,
    );
  },
};
